import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import fetcher from 'api/fetcher';
import { getFormattedNumber } from 'utils/clickme';
import { RefundReason } from 'utils/constants/admin';

import Button, { ButtonKind, ButtonScale } from 'bloko/blocks/button';
import FormattedNumericInput from 'bloko/blocks/formattedNumericInput';
import Select, { Option } from 'bloko/blocks/select';
import Text, { TextSize } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import useTrls from 'hooks/translation/useTrls';

import { sendRefund } from 'components/Admin/InvoiceDetail/Invoice/InvoiceBody/Refund/sendRefund';

import { InvoiceDetail } from 'store/models/invoiceDetail';
import { InvoiceDreamPayerType } from 'store/models/invoices';
import { useDispatch } from 'store/utils';

import styles from 'components/Admin/InvoiceDetail/Invoice/InvoiceBody/Refund/Refund.module.less';

interface RefundInfo {
    amount: number;
    availabilityToRefund: boolean;
    bonusAmount: number;
}

const TrlKeys = {
    amount: 'admin.Invoices.InvoicesDetail.Invoice.Body.Refund.amount',
    bonus: 'admin.Invoices.InvoicesDetail.Invoice.Body.Refund.bonus',
    input: {
        errors: {
            decimalLength: 'admin.Invoices.InvoicesDetail.Invoice.Body.Refund.input.decimalLength',
            notNumber: 'admin.Invoices.InvoicesDetail.Invoice.Body.Refund.input.notNumber',
        },
    },
    disabled: 'admin.Invoices.InvoicesTable.Invoice.Refund.disabled',
    reason: {
        label: 'admin.Invoices.InvoicesDetail.Refund.reason.label',
        reasons: {
            [RefundReason.User]: 'admin.Invoices.InvoicesDetail.Refund.reason.user',
            [RefundReason.Writeoff]: 'admin.Invoices.InvoicesDetail.Refund.reason.writeoff',
        },
    },
    button: 'admin.Invoices.InvoicesDetail.Invoice.Body.refundBtn',
};

export const Refund: FC<InvoiceDetail> = ({ id, payerType }) => {
    const dispatch = useDispatch();

    const isGift = payerType === InvoiceDreamPayerType.Gift;

    const [refund, setRefund] = useState<RefundInfo | null>(null);
    const [refundReason, setRefundReason] = useState<RefundReason>(RefundReason.User);
    const [refundInputValue, setRefundInputValue] = useState(String(refund?.amount || 0));
    const [isLoadRefund, setLoadRefund] = useState(false);

    const trl = useTrls();

    const handlerChange = useCallback(
        (val: string) => {
            if (!refund) return;
            let max = refund.amount;
            if (!max && isGift) {
                max = refund.bonusAmount;
            }
            const numberValue = parseFloat(val);
            if (isFinite(numberValue) && numberValue > max) val = String(max);
            setRefundInputValue(val);
        },
        [refund, setRefundInputValue, isGift]
    );

    const fetchRefund = useCallback((id: number) => {
        void fetcher.get<unknown, RefundInfo>(`/api/v1/backoffice/invoices/refund?invoice_id=${id}`).then((refund) => {
            setRefund(refund);
            setRefundInputValue(String(refund?.amount || 0));
        });
    }, []);

    const onClickRefund = useCallback(() => {
        dispatch(sendRefund(id, refundInputValue, setLoadRefund, fetchRefund, refundReason));
    }, [dispatch, fetchRefund, id, refundInputValue, refundReason]);

    useEffect(() => {
        fetchRefund(id);
    }, [fetchRefund, id]);

    if (!refund) {
        return null;
    }

    const amount = refund.amount;
    const availability = refund.availabilityToRefund;
    const bonus = refund.bonusAmount;

    return (
        <div className={styles.refund}>
            <div>
                <p>
                    {getFormattedNumber(amount)}
                    <span className={styles.refundLabel}>
                        {trl('admin.Invoices.InvoicesDetail.Invoice.Body.Refund.amount')}
                    </span>
                </p>
                <p>
                    {getFormattedNumber(bonus)}
                    <span className={styles.refundLabel}>
                        {trl('admin.Invoices.InvoicesDetail.Invoice.Body.Refund.bonus')}
                    </span>
                </p>
            </div>
            <div className={styles.refundActionsWrapper}>
                {!isGift && (
                    <>
                        <div>
                            <span>{trl(TrlKeys.reason.label)}</span>
                            <Select
                                scale="small"
                                onChange={(value: ChangeEvent<HTMLSelectElement>) =>
                                    setRefundReason(value.target.value as RefundReason)
                                }
                                defaultValue={RefundReason.User}
                            >
                                {Object.entries(TrlKeys.reason.reasons).map(([reason, trlKey]) => (
                                    <Option key={reason} value={reason}>
                                        {trl(trlKey)}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        <VSpacing base={2} />
                    </>
                )}
                <div className={styles.refundActions} data-qa="refund-actions">
                    <div className={styles.refundInput}>
                        <FormattedNumericInput
                            scale="small"
                            errors={{
                                decimalLength: trl(TrlKeys.input.errors.decimalLength),
                                notNumber: trl(TrlKeys.input.errors.notNumber),
                            }}
                            onChange={handlerChange}
                            value={refundInputValue}
                            decimalLength={2}
                            decimalMark={'.'}
                        />
                        {!availability && <Text size={TextSize.Small}>{trl(TrlKeys.disabled)}</Text>}
                    </div>
                    <Button
                        kind={ButtonKind.Primary}
                        scale={ButtonScale.Small}
                        disabled={!availability || isLoadRefund || !refundInputValue}
                        onClick={onClickRefund}
                    >
                        {trl(TrlKeys.button)}
                    </Button>
                </div>
            </div>
        </div>
    );
};
